"use client";

import { Card } from "@heroui/react";
import { ReactNode } from "react";
import Image from "next/image";

import { Description, Title } from "./section-heading";
import MotionCol from "./motion-col";

type SectionItem = {
  id: string;
  title: string;
  description: string;
  src: string;
  width: number;
  height: number;
  imageSide: "left" | "right";
};

type WorkPlacesSectionClientProps = {
  sections: SectionItem[];
};

export default function WorkPlacesSectionClient({
  sections,
}: WorkPlacesSectionClientProps) {
  return (
    <div className="container space-y-12 px-6 md:space-y-16">
      {sections.map((section) => (
        <PlacesWorkCard key={section.id}>
          <>
            {section.imageSide === "left" && (
              <MotionCol
                className="order-1 flex w-full items-center justify-center lg:order-none lg:w-1/2"
                side="left"
              >
                <InfoImage
                  alt={section.title}
                  height={section.height}
                  src={section.src}
                  width={section.width}
                />
              </MotionCol>
            )}

            <MotionCol
              className="order-2 flex flex-col justify-center md:p-8 lg:order-none lg:w-1/2"
              side={section.imageSide === "left" ? "right" : "left"}
            >
              <InfoContent
                description={section.description}
                title={section.title}
              />
            </MotionCol>

            {section.imageSide === "right" && (
              <MotionCol
                className="order-1 flex w-full items-center justify-center lg:order-none lg:w-1/2"
                side="right"
              >
                <InfoImage
                  alt={section.title}
                  height={section.height}
                  src={section.src}
                  width={section.width}
                />
              </MotionCol>
            )}
          </>
        </PlacesWorkCard>
      ))}
    </div>
  );
}

function PlacesWorkCard({ children }: { children: ReactNode }) {
  return (
    <Card className="flex h-full flex-col justify-between bg-transparent p-0! shadow-none md:p-8 lg:flex-row lg:p-10">
      {children}
    </Card>
  );
}

function InfoContent({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <>
      <Title className="text-primary-700">{title}</Title>
      <Description className="pt-3">{description}</Description>
    </>
  );
}

function InfoImage({
  src,
  alt = "",
  width,
  height,
  className = "",
}: {
  src?: string;
  alt?: string;
  width: number;
  height: number;
  className?: string;
}) {
  const imageAspectRatio = `${width} / ${height}`;

  if (!src) {
    return (
      <div
        className={`bg-primary-50 relative w-full max-w-lg rounded-xl ${className}`}
        style={{ aspectRatio: imageAspectRatio }}
      />
    );
  }

  return (
    <div
      className={`relative w-full max-w-lg overflow-hidden rounded-xl ${className}`}
      style={{ aspectRatio: imageAspectRatio }}
    >
      <Image
        fill
        alt={alt}
        className="object-contain p-4 sm:p-6 md:p-8"
        sizes="(min-width: 1024px) 50vw, (min-width: 768px) 80vw, 100vw"
        src={src}
      />
    </div>
  );
}
